// Admin moderation queue for user-generated article comments
// (epic PROP-73 / story PROP-80). Mirrors PropertyReviews.tsx
// moderation page (PROP-77) in shape — same three tabs, same
// reason-required action dialog, same stale-resolution guard via
// the cleanup-token `alive` flag.

import { useEffect, useState } from "react";
import { PageHeader, primaryBtn, ghostBtn } from "@/components/admin-ui";
import { Modal, useToast } from "@/components/ui-prims";
import {
  deleteCommentAsAdmin,
  hideComment,
  listModerationComments,
  restoreComment,
  type ModerationComment,
  type ModerationFilter,
} from "@/lib/article-comments-api";

type Verb = "hide" | "restore" | "delete";

const TABS: { key: ModerationFilter; label: string }[] = [
  { key: "reported", label: "Reported" },
  { key: "hidden", label: "Hidden" },
  { key: "all", label: "All" },
];

const PAGE_LIMIT = 30;

export function ArticleCommentsModerationPage() {
  const toast = useToast();

  const [filter, setFilter] = useState<ModerationFilter>("reported");
  const [rows, setRows] = useState<ModerationComment[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadingMore, setLoadingMore] = useState(false);

  const [pending, setPending] = useState<{ row: ModerationComment; verb: Verb } | null>(null);
  const [reason, setReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setRows([]);
    setNextCursor(undefined);
    setExpanded(new Set());
    listModerationComments({ filter, limit: PAGE_LIMIT })
      .then((resp) => {
        if (!alive) return;
        setRows(resp.comments ?? []);
        setNextCursor(resp.next_cursor);
        setStatus("ready");
      })
      .catch((err) => {
        if (!alive) return;
        console.error("listModerationComments", err);
        setStatus("error");
      });
    return () => {
      alive = false;
    };
  }, [filter]);

  const onLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const resp = await listModerationComments({ filter, limit: PAGE_LIMIT, cursor: nextCursor });
      setRows((prev) => [...prev, ...(resp.comments ?? [])]);
      setNextCursor(resp.next_cursor);
    } catch (err) {
      console.error("listModerationComments more", err);
      toast("Couldn't load more comments", { tone: "error" });
    } finally {
      setLoadingMore(false);
    }
  };

  const askAction = (row: ModerationComment, verb: Verb) => {
    setPending({ row, verb });
    setReason("");
    setActionErr(null);
  };

  const confirmAction = async () => {
    if (!pending) return;
    const trimmed = reason.trim();
    if (trimmed === "") {
      setActionErr("Reason required — explain why this action is being taken so the audit row reads cleanly.");
      return;
    }
    setActionBusy(true);
    setActionErr(null);
    const { row, verb } = pending;
    try {
      const resp =
        verb === "hide"
          ? await hideComment(row.id, trimmed)
          : verb === "restore"
          ? await restoreComment(row.id, trimmed)
          : await deleteCommentAsAdmin(row.id, trimmed);
      setRows((prev) => {
        const patched = prev.map((r) => (r.id === row.id ? resp.comment : r));
        return filterRowsForTab(patched, filter);
      });
      setPending(null);
      setReason("");
      toast(`Comment ${verbPastTense(verb)} · audit logged`, { tone: "success" });
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e?.status === 404) {
        setActionErr("This comment has already been removed by another moderator.");
      } else {
        setActionErr(e?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setActionBusy(false);
    }
  };

  const cancelAction = () => {
    if (actionBusy) return;
    setPending(null);
    setReason("");
    setActionErr(null);
  };

  return (
    <>
      <PageHeader
        kicker="Moderation"
        title="Article comments"
        sub="Reported / hidden user comments on published articles. Hide drops the row from the public discussion and decrements article_stats.comments; Restore puts it back; Delete is a soft-delete that surfaces only in the audit trail. Every action requires a reason that lands in admin_audit_log."
      />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, padding: "0 24px", borderBottom: "1px solid var(--rule)" }}>
        {TABS.map((tab) => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                cursor: "pointer",
                fontFamily: "var(--font-ui)",
                fontSize: 12.5,
                fontWeight: active ? 500 : 400,
                color: active ? "var(--accent)" : "var(--ink-3)",
                borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ padding: "16px 24px 32px" }}>
        {status === "loading" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Loading comments…</p>
        ) : status === "error" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)" }}>Couldn't load comments. Refresh to try again.</p>
        ) : rows.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Nothing in this queue right now.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-ui)" }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: 10.5, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                <th style={th()}>Article</th>
                <th style={th()}>Author</th>
                <th style={{ ...th(), width: "45%" }}>Body</th>
                <th style={th()}>Status</th>
                <th style={th()}>Reports</th>
                <th style={th()}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <CommentRow
                  key={r.id}
                  row={r}
                  expanded={expanded.has(r.id)}
                  onToggleExpanded={() => toggleExpanded(r.id)}
                  onAction={(verb) => askAction(r, verb)}
                />
              ))}
            </tbody>
          </table>
        )}

        {nextCursor && status === "ready" && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <button type="button" style={ghostBtn} onClick={() => void onLoadMore()} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load older comments"}
            </button>
          </div>
        )}
      </div>

      <Modal
        open={!!pending}
        onClose={cancelAction}
        title={pending ? dialogTitle(pending.verb) : ""}
        width={520}
        footer={
          <>
            <button type="button" style={ghostBtn} onClick={cancelAction} disabled={actionBusy}>Cancel</button>
            <button
              type="button"
              style={{ ...primaryBtn, background: pending?.verb === "delete" ? "#7a2d1f" : primaryBtn.background }}
              onClick={() => void confirmAction()}
              disabled={actionBusy}
            >
              {actionBusy ? "Working…" : pending ? dialogConfirm(pending.verb) : ""}
            </button>
          </>
        }
      >
        {pending && (
          <>
            <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55, margin: "0 0 12px" }}>
              {dialogBody(pending.verb)}
            </p>
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 6, background: "var(--surface-2)", fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 }}>
              <strong>{pending.row.author_name || "Anonymous"}</strong> · article {pending.row.article_id.slice(0, 8)}
              <br />
              <span style={{ color: "var(--ink-3)" }}>{truncate(pending.row.body, 200)}</span>
            </div>
            <label style={{ display: "block", marginBottom: 8 }}>
              <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Reason <span style={{ color: "#7a2d1f" }}>*</span>
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Why this action? — recorded in admin_audit_log so the next moderator can trace it."
                disabled={actionBusy}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--rule)", fontSize: 13, fontFamily: "var(--font-ui)", background: "var(--surface)", color: "var(--ink)", resize: "vertical", minHeight: 70 }}
              />
            </label>
            {actionErr && <p style={{ fontSize: 12, color: "#7a2d1f", margin: "4px 0 0" }}>{actionErr}</p>}
          </>
        )}
      </Modal>
    </>
  );
}

function CommentRow({
  row, expanded, onToggleExpanded, onAction,
}: {
  row: ModerationComment;
  expanded: boolean;
  onToggleExpanded: () => void;
  onAction: (verb: Verb) => void;
}) {
  return (
    <>
      <tr style={{ borderTop: "1px solid var(--rule)" }}>
        <td style={td()}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>{row.article_id.slice(0, 8)}</div>
          <div style={{ fontSize: 10.5, color: "var(--ink-4)", marginTop: 2 }}>{relativeTime(row.created_at)}</div>
        </td>
        <td style={td()}>
          <div style={{ fontSize: 12, color: "var(--ink-2)", overflowWrap: "anywhere" }}>{row.author_name || "Anonymous"}</div>
          <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>{row.author_user_id.slice(0, 8)}</div>
        </td>
        <td style={{ ...td(), fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
          <span style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" } as React.CSSProperties}>
            {row.body}
          </span>
        </td>
        <td style={td()}>
          <StatusPill status={row.status} />
        </td>
        <td style={td()}>
          {row.reports.length > 0 ? (
            <button type="button" onClick={onToggleExpanded} style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "var(--ink-2)", cursor: "pointer", fontFamily: "var(--font-ui)" }}>
              {row.reports.length} {expanded ? "▴" : "▾"}
            </button>
          ) : (
            <span style={{ fontSize: 11, color: "var(--ink-4)" }}>0</span>
          )}
        </td>
        <td style={td()}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {row.status === "visible" && <ActionBtn onClick={() => onAction("hide")}>Hide</ActionBtn>}
            {row.status === "hidden" && <ActionBtn onClick={() => onAction("restore")}>Restore</ActionBtn>}
            <ActionBtn danger onClick={() => onAction("delete")}>Delete</ActionBtn>
          </div>
        </td>
      </tr>
      {expanded && row.reports.length > 0 && (
        <tr>
          <td colSpan={6} style={{ padding: "0 12px 12px", background: "var(--surface-2)" }}>
            <div style={{ padding: 12, fontSize: 12, color: "var(--ink-2)" }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                Reporters ({row.reports.length})
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {row.reports.map((rp, i) => (
                  <li key={`${rp.reporter_user_id}-${i}`} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 500, color: "var(--ink-2)", flexShrink: 0 }}>{rp.reporter_name || "Anonymous"}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--accent)", flexShrink: 0 }}>{rp.reason}</span>
                    {rp.note && <span style={{ color: "var(--ink-3)", overflowWrap: "anywhere" }}>{rp.note}</span>}
                    <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--ink-4)", flexShrink: 0 }}>{relativeTime(rp.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ActionBtn({ children, danger, onClick }: { children: React.ReactNode; danger?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: danger ? "transparent" : "var(--accent-soft)",
        border: `1px solid ${danger ? "#7a2d1f" : "var(--accent)"}`,
        color: danger ? "#7a2d1f" : "var(--accent)",
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 11,
        fontWeight: 500,
        fontFamily: "var(--font-ui)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function StatusPill({ status }: { status: ModerationComment["status"] }) {
  const palette =
    status === "visible"
      ? { bg: "var(--accent-soft)", fg: "var(--accent)" }
      : status === "hidden"
      ? { bg: "#f0e7d0", fg: "#6b5320" }
      : { bg: "#ecd9d2", fg: "#7a2d1f" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 12,
        background: palette.bg,
        color: palette.fg,
        fontSize: 10.5,
        fontWeight: 500,
        letterSpacing: "0.02em",
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
}

function th(): React.CSSProperties {
  return { padding: "10px 8px", borderBottom: "1px solid var(--rule)", fontWeight: 500 };
}
function td(): React.CSSProperties {
  return { padding: "12px 8px", verticalAlign: "top" };
}
function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + "…";
}
function verbPastTense(v: Verb): string {
  return v === "hide" ? "hidden" : v === "restore" ? "restored" : "deleted";
}
function dialogTitle(v: Verb): string {
  return v === "hide" ? "Hide this comment?" : v === "restore" ? "Restore this comment?" : "Delete this comment?";
}
function dialogConfirm(v: Verb): string {
  return v === "hide" ? "Hide comment" : v === "restore" ? "Restore comment" : "Delete comment";
}
function dialogBody(v: Verb): string {
  switch (v) {
    case "hide":
      return "Pulls the comment out of the public discussion immediately. Decrements article_stats.comments in the same transaction. Hidden rows stay in the queue under the Hidden tab.";
    case "restore":
      return "Puts the comment back in the public discussion. Increments article_stats.comments. The reports against it remain as moderation history.";
    case "delete":
      return "Soft-delete — the body is kept for the audit trail but the row no longer surfaces in any tab. Reserve for the worst cases; Hide is the normal moderation action.";
  }
}

function filterRowsForTab(rows: ModerationComment[], filter: ModerationFilter): ModerationComment[] {
  switch (filter) {
    case "reported":
      return rows.filter((r) => r.status === "visible" && r.report_count > 0);
    case "hidden":
      return rows.filter((r) => r.status === "hidden");
    case "all":
      return rows.filter((r) => r.status === "visible" || r.status === "hidden");
  }
}

function relativeTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, (Date.now() - t) / 60000);
  if (diffMin < 60) return `${Math.round(diffMin)} min ago`;
  const diffHr = diffMin / 60;
  if (diffHr < 24) return `${Math.round(diffHr)} hrs ago`;
  const diffDay = diffHr / 24;
  if (diffDay < 7) return `${Math.round(diffDay)} days ago`;
  return new Date(iso).toLocaleDateString();
}
